import { useCallback, useMemo, useRef, useState } from "react";
import Button from "../../../../components/shared/Button/Button";
import Tooltip from "../../../../components/shared/Tooltip/Tooltip";
import { useSwingLibraryStore } from "../../store/swingLibraryStore";
import { PdfPagesRenderer } from "./components/PdfPagesRenderer";
import { ReportGenerationDialog } from "./components/ReportGenerationDialog";
import { usePdfData } from "./hooks/usePdfData";
import { usePdfGeneration } from "./hooks/usePdfGeneration";
import { usePdfVerification } from "./hooks/usePdfVerification";

export const GeneratePdfButton = () => {
  const { selectedVideos } = useSwingLibraryStore();

  const isTest = false;

  // Create refs for PDF pages
  const mainPageRef = useRef(null);
  const chartPageRef = useRef(null);
  const speedPage1Ref = useRef(null);
  const speedPage2Ref = useRef(null);
  const efficiency1PageRef = useRef(null);
  const consistency1PageRef = useRef(null);
  const consistency2PageRef = useRef(null);

  const pageRefs = useMemo(
    () => [
      mainPageRef,
      chartPageRef,
      speedPage1Ref,
      speedPage2Ref,
      efficiency1PageRef,
      consistency1PageRef,
      consistency2PageRef,
    ],
    [
      mainPageRef,
      chartPageRef,
      speedPage1Ref,
      speedPage2Ref,
      efficiency1PageRef,
      consistency1PageRef,
      consistency2PageRef,
    ]
  );

  // Custom hooks for separation of concerns
  const { selectedTableData, verificationState } = usePdfVerification({
    selectedVideos,
  });
  const {
    actionSdkResponse,
    fetchDataForGeneration,
    firstPageReportData,
    hasFetchingFailed,
  } = usePdfData();

  // Extract user data from selected videos
  const gender = selectedTableData[0]?.userData?.gender;
  const golfClub = selectedTableData[0]?.userData?.golfClub;
  const height = selectedTableData[0]?.userData?.height;
  const userName = selectedTableData[0]?.userData?.fullName;
  const userId = selectedTableData[0]?.parentUserId;
  const videoIds = useMemo(
    () => selectedTableData?.map((el) => el.id),
    [selectedTableData]
  );

  // PDF generation hook
  const { generatePDF } = usePdfGeneration({
    actionSdkResponse,
    golfClub,
    userId,
  });

  const [openDialog, setOpenDialog] = useState(false);

  const startPdfGenerationPipeline = useCallback(async () => {
    await fetchDataForGeneration(videoIds, golfClub, gender, height);
  }, [fetchDataForGeneration, videoIds, golfClub, gender, height]);

  const startPdfDownload = useCallback(async () => {
    const reportURL = await generatePDF(userName, pageRefs);
    return reportURL;
  }, [generatePDF, userName, pageRefs]);

  const notEnoughSwings = !videoIds || videoIds.length < 3;

  return (
    <>
      <Tooltip
        content={
          notEnoughSwings
            ? "Select 3 or more swings to generate a Swing Score report."
            : ""
        }
        placement="bottom"
      >
        <Button
          color="white/hover:shadow"
          rounded="md"
          className="font-normal w-full flex items-center justify-start"
          isDisabled={notEnoughSwings}
          onClick={
            isTest
              ? () => startPdfGenerationPipeline()
              : () => setOpenDialog(true)
          }
        >
          Score Report
        </Button>
      </Tooltip>

      {isTest && (
        <Button color="gray100" onClick={startPdfDownload}>
          download report
        </Button>
      )}

      {/* Dialog (ModalMobile under the hood) */}
      <ReportGenerationDialog
        onClose={() => setOpenDialog(false)}
        onCreditSpent={startPdfDownload}
        onPrepare={startPdfGenerationPipeline}
        open={openDialog}
        verificationState={verificationState}
        hasFetchingFailed={hasFetchingFailed}
      />

      {/* Offscreen PDF page rendering */}
      <PdfPagesRenderer
        actionSdkResponse={actionSdkResponse}
        chartPageRef={chartPageRef}
        consistency1PageRef={consistency1PageRef}
        consistency2PageRef={consistency2PageRef}
        efficiency1PageRef={efficiency1PageRef}
        firstPageReportData={firstPageReportData}
        golfClub={golfClub}
        isTest={isTest}
        mainPageRef={mainPageRef}
        speedPage1Ref={speedPage1Ref}
        speedPage2Ref={speedPage2Ref}
        userName={userName}
        videoIds={videoIds}
      />
    </>
  );
};
